<template>
  <div class="news-view-wrapper" :class="[{'news-view-wrapper--landing-page' : newsHome}, animationStatus]">
    <slot name="close-btn-overlay"></slot>
    <div v-swiper:newsSwiper="swiperOption" :key="newsHome ? 'news-home' : 'news-node'" ref="newsSwiper"
         @touchStart="$emit('start-moving')"
         @transitionEnd="onSlideChange()" v-if="!newsHome">
      <div class="swiper-wrapper">
        <template v-for="(nid, i) in nids">
          <newspaper
            v-if="includes(filteredNids, nid)"
            class="swiper-slide"
            :index="i"
            :totalSlide="totalSlide"
            :nid="nid"
            :key="nid"
            :isActive="(activeIndex === i)"
            :activeIndex="activeIndex"
            :shareUrl="shareUrl"
            @newspaperOnScroll="onNewspaperOnScroll"
            @newspaperOnScrollStop="isShowArrow = true"
          ></newspaper>
          <div v-else class="invisible-wrapper newspaper-wrapper swiper-slide"></div>
        </template>
      </div>
    </div>
    <div v-else class="show-landing-page">
      <landing-page :nids="orgNids" :nid="nid" @destroyLandingPage="destroyLandingPage()"></landing-page>
    </div>
    <slot name="close-btn"></slot>
    <toggle-share-buttons
      v-show="swiperIsReady"
      class="toggle-share-wrapper hidden-sm-down"
      :class="{'slide-moving': slideMoving}"
      :shareUrl="shareUrl"
      mode="Tech City"
      :title="seoTitle"
      @shareCountFetched="true"
      @shareHover="shareHover"
      :bypassUserInteraction="true"
      iconDesktop="down"
      :gtmCustomEvents="{ec: 'Outbound Clicks', ea: 'China Tech City News/Share/Click'}"
    ></toggle-share-buttons>
    <news-view-nav-button
      :isArrowDisabled="index <= 0"
      :isShowArrow="isShowArrow"
      @newsViewNavButtonOnClick="slide('prev')"
      type="prev"
      v-gtm-tracker="{trackEV: false, ec: 'Article News/Arrow', ea: 'China Tech City News View/Arrow/Click', el: 'Previous'}"
      v-if="!newsHome"
    ></news-view-nav-button>
    <news-view-nav-button
      :isArrowDisabled="index >= nids.length - 1"
      :isShowArrow="isShowArrow"
      @newsViewNavButtonOnClick="slide('next')"
      type="next"
      v-if="!newsHome"
      v-gtm-tracker="{trackEV: false, ec: 'Article News/Arrow', ea: 'China Tech City News View/Arrow/Click', el: 'Next'}"
    ></news-view-nav-button>
  </div>
</template>

<script>
  import 'swiper/dist/css/swiper.css'
  import { isDesktop, getNodeQueueTid } from 'mixins/utilities'
  import get from 'lodash/get'
  import slice from 'lodash/slice'
  import includes from 'lodash/includes'
  import GTMManager from 'mixins/gtm-manager'

  import EventBus from 'components/helpers/event-bus'

  export default {
    name: 'news-view',
    model: {
      prop: 'index',
      event: 'indexChanged',
    },
    props: {
      nids: {
        type: Array,
        required: true,
      },
      orgNids: {
        type: Array,
        required: true,
      },
      shareOpts: {
        type: Object,
        required: false,
      },
      index: {
        type: Number,
      },
      slideMoving: {
        type: Boolean,
        default: false,
      },
      newsHome: {
        type: Boolean,
        default: false,
      },
    },
    components: {
      Newspaper: () => import('components/tech-city/news-view/Newspaper.vue'),
      LandingPage: () => import('components/tech-city/news-view/LandingPage.vue'),
      ToggleShareButtons: () => import('components/widgets/ToggleShareButtons.vue'),
      NewsViewNavButton: () => import('components/tech-city/news-view/NewsViewNavButton.vue'),
      TechCity: () => import('pages/TechCityView.vue'),
    },
    data() {
      return {
        slideFreeze: false,
        isShowArrow: true,
        activeIndex: 0,
        animationStatus: 'loading',
        slideChanged: false,
        slideBuffer: 2,
        swiperOption: {
          init: false,
          slidesPerView: 'auto',
          spaceBetween: 30,
          centeredSlides: true,
          preventClicksPropagation: false,
          preventClicks: false,
          effect: 'coverflow',
          coverflowEffect: {
            rotate: 50,
            stretch: 0,
            depth: 100,
            modifier: 1,
            slideShadows: false,
          },
          on: {
            init: this.slideInit,
            slideChange: () => { this.slideChanged = true },
            slideChangeTransitionStart: () => { this.$emit('start-moving') },
            transitionEnd: () => { this.$emit('end-moving') },
            touchStart: () => { this.$emit('start-moving') },
          },
        },
        swiperIsReady: false,
        noMore: false,
        landingPage: false,
      }
    },
    computed: {
      newsSwiperRef() {
        return get(this, '$refs.newsSwiper', {})
      },
      shareUrl() {
        return get(this, 'shareOpts.shareUrl', '')
      },
      seoTitle() {
        return get(this, 'shareOpts.seoTitle', '')
      },
      totalSlide() {
        return get(this, 'nids.length', 0)
      },
      filteredNids() {
        return slice(get(this, 'nids', []), this.sliceFrom, this.sliceTo)
      },
      sliceFrom() {
        return (get(this, 'activeIndex', 0) - get(this, 'slideBuffer', 0)) > 0 ? get(this, 'activeIndex', 0) - get(this, 'slideBuffer', 0) : 0
      },
      sliceTo() {
        return get(this, 'activeIndex', 0) + get(this, 'slideBuffer', 0) + 1 < this.nids.length ? get(this, 'activeIndex', 0) + get(this, 'slideBuffer', 0) + 1 : this.nids.length
      },
    },
    mounted() {
      if (this.newsHome) {
        this.initLandingPage()
      } else {
        this.$emit('newsViewLoaded')
      }
    },
    methods: {
      includes,
      swiperInit() {
        this.newsSwiper.init()
      },
      close() {
        this.animationStatus = 'closing'
      },
      trackArticleSwipe(direction) {
        const label = direction === 'left' ? 'Article/Swipe/Left' : 'Article/Swipe/Right'
        GTMManager.pushTracker({
          category: 'Article News',
          action: 'China Tech City News View/Article/Swipe',
          label,
        })
      },
      slide(direction) {
        if (this.newsSwiperRef.swiper && this.slideFreeze === false) {
          this.slideChanged = true
          switch (direction) {
          case 'prev': {
            this.newsSwiperRef.swiper.slidePrev()
            break
          }
          case 'next': {
            this.newsSwiperRef.swiper.slideNext()
            break
          }
          default: break
          }
        }
      },
      onSlideChange() {
        if (this.slideChanged) {
          this.slideFreeze = true
          this.activeIndex = get(this, 'newsSwiperRef.swiper.activeIndex', 0)

          const nid = get(this, `nids[${this.activeIndex}]`)
          // If click article nav, index === active index, so this wont be triggered
          // If swipe, index will be different from active index
          if (this.activeIndex > this.index) {
            this.trackArticleSwipe('right')
          } else if (this.activeIndex < this.index) {
            this.trackArticleSwipe('left')
          }

          // Update parent's component data
          this.$emit('indexChanged', this.activeIndex)
          const activeNode = this.$store.getters.node(nid)

          const routeData = {
            type: 'techCityNews',
            props: {
              nid,
            },
          }
          this.$router.addRouteByNode({routeData, node: activeNode}).then(() => {
            this.slideFreeze = false
            this.$router.push(routeData.url)
            EventBus.$emit('routeChangeEnd')
          })
          const bufferForNids = this.nids.length - 1 - 2 // for secure, make sure nids has enough nid
          if (bufferForNids > 0 && this.activeIndex >= bufferForNids) this.fetchArticle() // prepare for load on demand
        }
        this.slideChanged = false
      },
      slideInit() {
        this.swiperIsReady = true
        this.$emit('newsViewReady')
        this.$nextTick(function () {
          setTimeout(() => {
            this.animationStatus = 'loaded'
            setTimeout(() => {
              this.animationStatus = 'finished'
            }, 500)
          }, 200)
        })
      },
      initLandingPage() {
        this.swiperIsReady = true
        this.$emit('newsViewReady')
        this.$nextTick(function () {
          setTimeout(() => {
            this.animationStatus = 'loaded'
            setTimeout(() => {
              this.animationStatus = 'finished'
            }, 2500)
          }, 200)
        })
      },
      shareHover(isShow) {
        if (isShow && isDesktop()) {
          GTMManager.pushTracker({
            category: 'Hover',
            action: 'China Tech City News View/Share/Hover',
          })
        }
      },
      onNewspaperOnScroll() {
        this.$emit('end-moving')
        this.isShowArrow = false
      },
      fetchArticle() {
        if (!this.noMore) {
          const limit = 10
          const offset = get(this, 'nids', []).length
          this.$store.dispatch('fetchNodeQueueDynamic', {
            tid: getNodeQueueTid('china_tech_city'),
            queueName: 'section_top',
            limit,
            offset,
          }).then(() => this.noMore = (get(this, 'nids', []).length < limit + offset))
        }
      },
      destroyLandingPage() {
        this.animationStatus = 'loaded'
        this.$emit('update-first-nid', this.nid)
        this.$nextTick(function () {
          this.swiperInit()
        })
      },
    },
  }
</script>

<style lang="scss" scoped>
  .news-view-wrapper {
    position: fixed;
    z-index: 2000;

    &.loading, &.loaded {
      .swiper-slide, /deep/ .show-landing-page {
        opacity: 0;
      }
      /deep/ {
         .news-view-nav-btn, .close-btn, .toggle-share-wrapper {
           opacity: 0;
         }
      }
      .swiper-container {
        transform: translateY(100%);
        transition: transform 300ms;
      }
      /deep/ .landing-wrapper {
        transform: translateY(100%);
        transition: transform 300ms;
      }
    }

    &.loaded {
      .swiper-slide-active, /deep/ .show-landing-page {
        opacity: 1;
      }
      .swiper-container {
        transform: translateY(0);
      }
      /deep/ .landing-wrapper {
        transform: translateY(0);
      }
    }

    &.closing {
      .swiper-container, /deep/ .landing-wrapper {
        transform: translateY(100%);
        transition: transform 300ms;
      }

      /deep/ {
         .news-view-nav-btn, .close-btn, .toggle-share-wrapper {
           opacity: 0;
         }
      }
    }

    @include respond-to(mobile) {
      top: rem-calc(59);
    }
    .swiper-container {
      height: 100%;
      overflow: initial;
      will-change: transform, opacity;
    }
    .swiper-wrapper {
      width: 100vw;
    }
    top: $header-height-mobile;
    @include respond-to("desktop") {
      top: rem-calc(100);
    }
    .close-news-view-helper {
      position: absolute;
      top: rem-calc(-100px);
      height: rem-calc(100px);
      left: rem-calc(86px);
      right: rem-calc(64px);

      @include respond-to("mobile") {
        left: rem-calc(53);
        right: rem-calc(47);
      }
    }
    .toggle-share-wrapper {
      cursor: pointer;
      position: absolute;
      z-index: 1;
      transition: 500ms;
      top: rem-calc(63);
      z-index: 10000;
      left: 50%;
      transform: translateX(calc(40vw + 15px));
      right: initial;

      @include respond-to("mobile") {
        display: none;
      }
      @include respond-to("hd") {
        right: initial;
        left: 50%;
        transform: translateX(rem-calc(640));
      }
      @include respond-to("largedesktop") {
        right: initial;
        left: 50%;
        transform: translateX(rem-calc(615));
      }
      &.slide-moving {
        opacity: 0.2;
      }
    }
  }

  .newspaper-wrapper {
    color: $greyish-brown;
    width: 80vw;
    height: 100%;
    position: relative;
    overflow: hidden;
    will-change: transform, opacity;
  }

  .show-landing-page {
    width: 100vw;
  }

  @include respond-to("mobile") {
    .news-view-wrapper--landing-page {
      .close-btn {
        fill: $undercooked-omelette;
        top: rem-calc(22);
        right: rem-calc(-2);
      }
    }
  }
</style>



// WEBPACK FOOTER //
// components/tech-city/news-view/NewsView.vue